import React, { ReactNode, useMemo, useState, useCallback, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { PrivateRoute } from '@feature/auth';
import { cn } from '@utils/utils';
import { useBackgroundManager } from '@shared/hooks/useBackgroundManager';
import { useWebSocketContext } from '@shared/contexts/WebSocketContext';

interface AppLayoutProps {
  children?: ReactNode;
}

export const AppLayout = React.memo(({ children }: AppLayoutProps) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  const { isConnected, connectionId } = useWebSocketContext();

  useEffect(() => {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('🔌 Estado WebSocket:', { isConnected, connectionId });
    }
  }, [isConnected, connectionId]);
  
  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen(prev => !prev);
  }, []);

  const location = useLocation();
  
  useBackgroundManager();
  
  const shouldShowHeader = useMemo(() => location.pathname !== '/home', [location.pathname]);

  const mainContentClasses = useMemo(() => cn(
    "relative transition-all duration-300",
    isSidebarOpen ? "ml-74" : "ml-20"
  ), [isSidebarOpen]);

  return (
    <PrivateRoute>
      <div className="relative h-screen overflow-hidden">
        {/* Sidebar con props */}
        <Sidebar 
          isOpen={isSidebarOpen} 
          onToggle={toggleSidebar}
        />
        {/* Main Content */}
        <div className={cn(mainContentClasses, "h-full flex flex-col")}>
          {/* Header - Solo se muestra si no estamos en home */}
          {shouldShowHeader && (
            <div className="pt-4 pb-4 ml-5 top-0 absolute z-10">
              <Header />
            </div>
          )}

          {/* Content Area */}
          <div className="px-7 flex-1 min-h-0 overflow-y-auto">
            {children || <Outlet />}
          </div>
        </div>
      </div>
    </PrivateRoute>
  );
});

AppLayout.displayName = 'AppLayout'; 